import { useEffect, useId, useMemo, useState } from "react";
import {
  ArrowDownRight,
  ArrowUpRight,
  ChevronDown,
  TrendingUp,
} from "lucide-react";
import {
  isLastPointInOrApproachingAlarmBand,
  SensorTrendChart,
  getSortedTrendPointsFromRow,
} from "@/components/root-cause-analysis/rca-measurement-trend-shared.jsx";
import { RcaDriftStatsPanel } from "@/components/root-cause-analysis/rca-trend-stats-panel.jsx";
import {
  DEFAULT_FIRST_WINDOW_PCT,
  DEFAULT_LAST_WINDOW_HOURS,
  DEFAULT_SHIFT_THRESHOLD_PCT,
  PreShutdownDriftSettingsToolbar,
  usePreShutdownDriftSettings,
} from "@/components/root-cause-analysis/pre-shutdown-drift-settings-toolbar.jsx";
import RcaMeasurementFilter from "@/components/root-cause-analysis/rca-measurement-filter.jsx";
import RcaMeasurementLabelHighlight from "@/components/root-cause-analysis/rca-measurement-label-highlight.jsx";
import { sortDriftEntries } from "@/components/root-cause-analysis/rca-measurement-sort.js";
import RcaMeasurementSortToolbar from "@/components/root-cause-analysis/rca-measurement-sort-toolbar.jsx";
import { Badge } from "@/components/ui/badge.jsx";
import {
  getMeasurementLabel,
  measurementLabelMatchesFilters,
} from "@/components/root-cause-analysis/root-cause-analysis-helpers.js";
import { useControllableState } from "@/components/root-cause-analysis/use-controllable-state.js";
import MiniTrendSparkline from "@/components/mini-trend-sparkline.jsx";
import { SERIES_COLORS } from "@/constants/chart-constants.js";
import {
  ALARM_PROXIMITY_ROW,
  getAlarmProximityBadgeClass,
  getAlarmProximityDotClass,
} from "@/lib/alarm-proximity-styles.js";
import { cn } from "@/lib/utils.js";

export {
  DEFAULT_FIRST_WINDOW_PCT,
  DEFAULT_LAST_WINDOW_HOURS,
} from "@/components/root-cause-analysis/pre-shutdown-drift-settings-toolbar.jsx";

/** @typedef {{ t: number, y: number }} TrendPoint */

/** Sparkline size for drift summary rows; grid column width is derived from this. */
const DRIFT_SUMMARY_SPARKLINE_WIDTH = 148;
const DRIFT_SUMMARY_SPARKLINE_HEIGHT = 32;

/** Header toggle button min width (15rem text floor + icon and gap). */
const DRIFT_HEADER_BUTTON_MIN_CLASS = "min-w-[calc(15rem+2.75rem)]";
/**
 * Toolbar horizontal-scroll floor: title min + stacked controls min + chevron + gaps.
 * Uses max(100%, …) so the row fills wide viewports but stops shrinking below this.
 */
const DRIFT_HEADER_TOOLBAR_MIN_CLASS =
  "min-w-[max(100%,calc(17.75rem+22.25rem+3.25rem))]";

/** Shared column tracks: tag | sparkline | stats | threshold | expand */
const DRIFT_ROW_GRID_CLASS =
  "grid w-full min-w-[40rem] items-center gap-x-2 gap-y-1 px-2 py-1.5 sm:gap-x-3 sm:px-2.5";

const DRIFT_ROW_CELL_CLASS = "min-w-0";

/**
 * @param {number} sparklineWidth
 */
function driftRowGridTemplateColumns(sparklineWidth) {
  return `minmax(6.5rem, 1.15fr) ${sparklineWidth}px minmax(11rem, 2fr) 4.75rem 1.5rem`;
}

/**
 * @param {number[]} values
 */
function meanOf(values) {
  if (values.length === 0) return NaN;
  return values.reduce((sum, v) => sum + v, 0) / values.length;
}

/**
 * Drift = (mean of last window − mean of first window) / observed range (max − min).
 * Qualifies when |mean_last − mean_first| is at least `shiftThresholdPct`% of that range.
 *
 * @param {MeasurementWithStatsRow|Record<string, unknown>} row
 * @param {{ firstWindowPct: number, lastWindowHours: number, shiftThresholdPct?: number }} options
 */
export function computeTrendDrift(row, options) {
  const firstWindowPct = Number(options.firstWindowPct);
  const lastWindowHours = Number(options.lastWindowHours);
  const shiftThresholdPct =
    Number.isFinite(Number(options.shiftThresholdPct))
    && Number(options.shiftThresholdPct) >= 0
      ? Number(options.shiftThresholdPct)
      : DEFAULT_SHIFT_THRESHOLD_PCT;

  const points = getSortedTrendPointsFromRow(row);
  if (points.length === 0) {
    return {
      drift: NaN,
      shift: NaN,
      qualifies: false,
      meanFirst: NaN,
      meanLast: NaN,
      range: NaN,
      up: true,
    };
  }

  const fraction =
    Number.isFinite(firstWindowPct)
    && firstWindowPct > 0
    && firstWindowPct < 100
      ? firstWindowPct / 100
      : DEFAULT_FIRST_WINDOW_PCT / 100;

  const lastHours =
    Number.isFinite(lastWindowHours) && lastWindowHours > 0
      ? lastWindowHours
      : DEFAULT_LAST_WINDOW_HOURS;

  const t0 = points[0].t;
  const tEnd = points[points.length - 1].t;
  const duration = tEnd - t0;
  const lastMs = lastHours * 60 * 60 * 1000;

  let firstPoints;
  let lastPoints;

  if (duration > 0) {
    const firstEndT = t0 + duration * fraction;
    const lastStartT = tEnd - lastMs;
    firstPoints = points.filter((p) => p.t <= firstEndT);
    lastPoints = points.filter((p) => p.t >= lastStartT);
  }

  if (!firstPoints?.length) {
    const n = Math.max(1, Math.floor(points.length * fraction));
    firstPoints = points.slice(0, n);
  }
  if (!lastPoints?.length) {
    lastPoints = points.filter((p) => p.t >= tEnd - lastMs);
    if (lastPoints.length === 0) {
      lastPoints = points.slice(-Math.max(1, Math.ceil(points.length * 0.1)));
    }
  }

  const meanFirst = meanOf(firstPoints.map((p) => p.y));
  const meanLast = meanOf(lastPoints.map((p) => p.y));
  const allY = points.map((p) => p.y);
  const range = Math.max(...allY) - Math.min(...allY);

  const shift = meanLast - meanFirst;
  const absShift = Math.abs(shift);
  const drift = range > 1e-12 ? shift / range : NaN;
  const shiftLimit = (shiftThresholdPct / 100) * range;

  const qualifies =
    Number.isFinite(shift) && range > 1e-12 && absShift >= shiftLimit;

  return {
    drift,
    shift,
    qualifies,
    meanFirst,
    meanLast,
    range,
    up: shift >= 0,
  };
}

/**
 * Latest numeric sample in the series (by time).
 * @param {MeasurementWithStatsRow|Record<string, unknown>} row
 */
function getLatestMeasurementValue(row) {
  const points = getSortedTrendPointsFromRow(row);
  if (points.length === 0) return NaN;
  return points[points.length - 1].y;
}

/**
 * @param {unknown[]} rows
 * @param {{ firstWindowPct: number, lastWindowHours: number, shiftThresholdPct?: number }} driftConfig
 */
function buildDriftEntries(rows, driftConfig) {
  if (!Array.isArray(rows)) return [];

  const out = rows
    .map((row) => {
      const driftResult = computeTrendDrift(row, driftConfig);
      const latestY = getLatestMeasurementValue(row);
      const approaching = isLastPointInOrApproachingAlarmBand(row);
      return {
        row,
        ...driftResult,
        latestY,
        approaching,
      };
    })
    .filter((e) => e.qualifies);

  return out;
}

/**
 * @param {number} drift
 */
function formatDriftLabel(drift) {
  if (!Number.isFinite(drift)) return "—";
  const pct = drift * 100;
  const a = Math.abs(pct);
  if (a !== 0 && (a < 0.01 || a >= 1000)) {
    return `${pct.toExponential(2)}%`;
  }
  return `${pct.toFixed(1)}%`;
}

/**
 * @param {{
 *   rows: unknown[],
 *   overallFilterText?: string,
 *   filterText?: string,
 *   onFilterTextChange?: (value: string) => void,
 *   hoursBack: number,
 *   useAlarmLimits?: boolean,
 *   sortMode?: import("@/components/root-cause-analysis/rca-measurement-sort.js").RcaMeasurementSortMode,
 *   onSortModeChange?: (mode: import("@/components/root-cause-analysis/rca-measurement-sort.js").RcaMeasurementSortMode) => void,
 *   sortDirection?: import("@/components/root-cause-analysis/rca-measurement-sort.js").RcaMeasurementSortDirection,
 *   onSortDirectionChange?: (direction: import("@/components/root-cause-analysis/rca-measurement-sort.js").RcaMeasurementSortDirection) => void,
 *   expanded?: boolean,
 *   onExpandedChange?: (expanded: boolean) => void,
 * }} props
 */
export default function PreShutdownDriftSummary({
  rows,
  overallFilterText = "",
  filterText: filterTextProp,
  onFilterTextChange,
  hoursBack,
  useAlarmLimits = true,
  sortMode: sortModeProp,
  onSortModeChange,
  sortDirection: sortDirectionProp,
  onSortDirectionChange,
  expanded: expandedProp,
  onExpandedChange,
}) {
  const [expanded, setExpanded] = useControllableState(
    expandedProp,
    onExpandedChange,
    true
  );
  const [filterText, setFilterText] = useControllableState(
    filterTextProp,
    onFilterTextChange,
    ""
  );
  const [sortMode, setSortMode] = useControllableState(
    sortModeProp,
    onSortModeChange,
    /** @type {import("@/components/root-cause-analysis/rca-measurement-sort.js").RcaMeasurementSortMode} */ (
      "score"
    )
  );
  const [sortDirection, setSortDirection] = useControllableState(
    sortDirectionProp,
    onSortDirectionChange,
    /** @type {import("@/components/root-cause-analysis/rca-measurement-sort.js").RcaMeasurementSortDirection} */ (
      "desc"
    )
  );

  function toggleExpanded() {
    setExpanded(!expanded);
  }

  const {
    driftConfig,
    firstWindowLabel,
    lastWindowLabel,
    shiftThresholdPct,
    toolbarProps,
  } = usePreShutdownDriftSettings();

  const [openDriftRowKeys, setOpenDriftRowKeys] = useState(
    /** @type {Set<string>} */ (new Set())
  );
  const headingId = useId();
  const driftRowPanelPrefix = useId();

  const allEntries = useMemo(
    () => buildDriftEntries(rows, driftConfig),
    [rows, driftConfig]
  );

  const entries = useMemo(
    () => {
      const filtered = allEntries.filter(({ row }) => {
        const label = getMeasurementLabel(row);
        return measurementLabelMatchesFilters(label, {
          sectionFilterText: filterText,
          overallFilterText,
        });
      });

      return sortDriftEntries(filtered, { sortMode, sortDirection });
    },
    [allEntries, sortMode, sortDirection, filterText, overallFilterText]
  );
  const filteredCount = entries.length;
  const totalCount = allEntries.length;
  const hoursPhrase = hoursBack === 1 ? "1 hour" : `${Number(hoursBack)} hours`;

  useEffect(() => {
    setOpenDriftRowKeys(new Set());
  }, [rows, driftConfig]);

  /**
   * @param {string} key
   */
  function toggleDriftRow(key) {
    setOpenDriftRowKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }

  if (!Array.isArray(rows) || rows.length === 0) {
    return null;
  }

  return (
    <section className='min-w-0 rounded-lg border border-border bg-muted/60 px-2 py-2 sm:px-3'>
      <div className='-mx-2 overflow-x-auto px-2 sm:-mx-3 sm:px-3'>
        <div
          className={cn(
            "flex w-full flex-nowrap items-start gap-x-2",
            DRIFT_HEADER_TOOLBAR_MIN_CLASS
          )}>
          <button
            type='button'
            className={cn(
              "flex flex-1 items-start gap-3 rounded-md text-left transition-colors hover:bg-muted/80 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/60",
              DRIFT_HEADER_BUTTON_MIN_CLASS
            )}
            onClick={toggleExpanded}
            aria-expanded={expanded}
            aria-controls={expanded ? `${headingId}-panel` : undefined}
            id={headingId}>
            <TrendingUp
              className='mt-0.5 h-5 w-5 shrink-0 text-foreground'
              aria-hidden
            />
            <div className='min-w-0 flex-1'>
              <h3 className='text-sm font-semibold text-foreground'>
                Pre-Shutdown Drift Summary ({filteredCount} / {totalCount})
              </h3>
              <p className='mt-1 text-xs text-muted-foreground'>
                Drift = (mean of last {lastWindowLabel} − mean of first{" "}
                {firstWindowLabel}) ÷ observed range (max − min). Listed when
                |shift| ≥ {shiftThresholdPct}% of that range ({hoursPhrase}{" "}
                window).
              </p>
            </div>
          </button>
          <PreShutdownDriftSettingsToolbar
            {...toolbarProps}
            onClick={(e) => e.stopPropagation()}
          />
          <button
            type='button'
            className='flex h-9 w-9 shrink-0 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-muted/80 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/60'
            onClick={toggleExpanded}
            aria-expanded={expanded}
            aria-controls={expanded ? `${headingId}-panel` : undefined}
            aria-label={
              expanded ? "Collapse drift summary" : "Expand drift summary"
            }>
            <ChevronDown
              className={cn(
                "h-6 w-6 transition-transform",
                expanded && "rotate-180"
              )}
              aria-hidden
            />
          </button>
        </div>
      </div>

      {expanded ? (
        <div
          id={`${headingId}-panel`}
          className='mt-4 border-t border-border/80 pt-4'
          aria-labelledby={headingId}>
          <div className='mb-3 flex flex-wrap items-center justify-between gap-2'>
            <RcaMeasurementFilter
              filterText={filterText}
              onFilterTextChange={setFilterText}
            />
            <RcaMeasurementSortToolbar
              sortMode={sortMode}
              onSortModeChange={setSortMode}
              sortDirection={sortDirection}
              onSortDirectionChange={setSortDirection}
              scoreLabel='Drift'
            />
          </div>
          {filteredCount === 0 ? (
            <p className='text-xs text-muted-foreground'>
              {filterText.trim()
                ? "No measurements match the current filter."
                : `No sensors met the drift threshold (|shift| ≥ ${shiftThresholdPct}% of observed range) for this window.`}
            </p>
          ) : (
              <ul className='flex list-none flex-col gap-2 overflow-x-auto p-0'>
              {entries.map(
                (
                  { row, drift, shift, up, approaching, meanFirst, meanLast },
                  index
                ) => {
                  const rowKey = String(row.AssetTagId ?? `idx-${index}`);
                  const rowOpen = openDriftRowKeys.has(rowKey);
                  const shiftFinite = Number.isFinite(shift);
                  const Arrow = up ? ArrowUpRight : ArrowDownRight;
                  const label = getMeasurementLabel(row);
                  const highlightedLabel = (
                    <RcaMeasurementLabelHighlight
                      label={label}
                      filterText={filterText.trim() || overallFilterText}
                    />
                  );
                  const uom = String(row.UoM ?? "").trim();
                  const driftLabel = formatDriftLabel(drift);
                  const lineColor =
                    SERIES_COLORS[index % SERIES_COLORS.length] ?? "#0ea5e9";
                  const chartPanelId = `${driftRowPanelPrefix}-${rowKey}`;

                  return (
                    <li
                      key={rowKey}
                      className={cn(
                        "flex flex-col overflow-hidden rounded-md border",
                        approaching
                          ? ALARM_PROXIMITY_ROW.surface
                          : "border-border bg-card"
                      )}>
                      <button
                        type='button'
                        className={cn(
                          DRIFT_ROW_GRID_CLASS,
                          "cursor-pointer text-left transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/60 focus-visible:ring-offset-2 focus-visible:ring-offset-transparent",
                          approaching
                            ? ALARM_PROXIMITY_ROW.hover
                            : "hover:bg-muted/80"
                        )}
                        style={{
                          gridTemplateColumns: driftRowGridTemplateColumns(
                            DRIFT_SUMMARY_SPARKLINE_WIDTH
                          ),
                        }}
                        onClick={() => toggleDriftRow(rowKey)}
                        aria-expanded={rowOpen}
                        aria-controls={rowOpen ? chartPanelId : undefined}>
                        <div
                          className={cn(
                            DRIFT_ROW_CELL_CLASS,
                            "flex items-center gap-1.5"
                          )}>
                          <Arrow
                            className={cn(
                              "h-4 w-4 shrink-0",
                              !shiftFinite
                                ? "text-muted-foreground"
                                : up
                                  ? "text-emerald-600 dark:text-emerald-400"
                                  : "text-red-600 dark:text-red-400"
                            )}
                            aria-hidden
                          />
                          <span className='min-w-0 break-words text-xs font-medium leading-snug text-foreground'>
                            {highlightedLabel}
                            {uom ? (
                              <span className='font-light text-muted-foreground'>
                                {" "}
                                ({uom})
                              </span>
                            ) : null}
                          </span>
                        </div>
                        <div
                          className='flex shrink-0 items-center justify-center'
                          style={{ width: DRIFT_SUMMARY_SPARKLINE_WIDTH }}>
                          <MiniTrendSparkline
                            rawData={row}
                            width={DRIFT_SUMMARY_SPARKLINE_WIDTH}
                            height={DRIFT_SUMMARY_SPARKLINE_HEIGHT}
                            strokeColor={lineColor}
                          />
                        </div>
                        <div className={DRIFT_ROW_CELL_CLASS}>
                          <RcaDriftStatsPanel
                            row={row}
                            meanFirst={meanFirst}
                            meanLast={meanLast}
                            driftLabel={driftLabel}
                            useAlarmLimits={useAlarmLimits}
                          />
                        </div>
                        <div
                          className={cn(
                            DRIFT_ROW_CELL_CLASS,
                            "flex items-center justify-center"
                          )}>
                          {approaching ? (
                            <Badge
                              variant='status'
                              className={getAlarmProximityBadgeClass(
                                "approaching",
                                "shrink-0 px-2.5 py-0.5 text-[10px] uppercase tracking-wide"
                              )}>
                              <span
                                className={getAlarmProximityDotClass(
                                  "approaching",
                                  "h-1.5 w-1.5"
                                )}
                                aria-hidden='true'
                              />
                              threshold
                            </Badge>
                          ) : null}
                        </div>
                        <div className='flex items-center justify-center'>
                          <ChevronDown
                            className={cn(
                              "h-5 w-5 shrink-0 text-muted-foreground transition-transform",
                              rowOpen && "rotate-180"
                            )}
                            aria-hidden
                          />
                        </div>
                      </button>
                      {rowOpen ? (
                        <div
                          id={chartPanelId}
                          className='border-t border-border/80 bg-card p-1.5 sm:p-2'>
                          <div className='min-h-0 w-full min-w-0'>
                            <SensorTrendChart
                              row={row}
                              lineColor={lineColor}
                              showAlarmLimits={useAlarmLimits}
                            />
                          </div>
                        </div>
                      ) : null}
                    </li>
                  );
                }
              )}
              </ul>
          )}
        </div>
      ) : null}
    </section>
  );
}
